import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import type { ParseRichTextParameter, TableOfContentsLinks } from '../types';
import {
    createDOMElementIdFromString,
    getCombinedTextValueOfChildTextNodes,
    richTextParserOptions,
} from './richTextParserOptions/richTextParserOptions';

type ParseRichTextOptions = {
    renderHeading1AsHeading2?: boolean;
};

export const parseRichText = (richTextDocument: ParseRichTextParameter, options?: ParseRichTextOptions) => {
    const renderNode = richTextParserOptions.renderNode ?? {};

    return documentToReactComponents(richTextDocument, {
        ...richTextParserOptions,
        renderNode: {
            ...renderNode,
            ...(options?.renderHeading1AsHeading2 && {
                [BLOCKS.HEADING_1]: renderNode[BLOCKS.HEADING_2],
            }),
        },
    });
};

export const parseRichContentToPlainText = (richTextDocument: ParseRichTextParameter) =>
    documentToPlainTextString(richTextDocument);

/**
 * Keeping things simple as we only need to support a single use case.
 *
 * If we ever get to a situation where we need to parse table of contents
 * differently in different components, this method and possibly parseRichText
 * method should be extended.
 */
export function parseTableOfContentsLinks(richText: ParseRichTextParameter) {
    return richText.content.reduce<TableOfContentsLinks[]>((links, topLevelBlock) => {
        if (topLevelBlock.nodeType === 'heading-2') {
            const text = getCombinedTextValueOfChildTextNodes(topLevelBlock.content);
            const id = createDOMElementIdFromString(text);
            return [
                ...links,
                {
                    text,
                    id,
                },
            ];
        }
        return links;
    }, []);
}

/**
 * Use for FAQ that does not rely on the accordion component, bur rather on
 * rich text where questions are H3 and answers are plain paragraphs.
 */
export function parseFaqRichText(richTextDocument: ParseRichTextParameter | undefined) {
    if (!richTextDocument) {
        return;
    }

    const questionIndices = richTextDocument.content
        .map((item, index) => {
            if (item.nodeType === 'heading-3') {
                return index;
            }
        })
        .filter(item => item !== undefined);

    const faqArray = questionIndices
        .map((item, index) => {
            const question = richTextDocument.content.at(Number(item));
            const answerParagraphs = richTextDocument.content.slice(Number(item) + 1, questionIndices.at(index + 1));

            if (question && answerParagraphs.length) {
                return {
                    question: documentToPlainTextString(question),
                    answer: answerParagraphs.map(item => documentToPlainTextString(item)).join(' '),
                };
            }
        })
        .filter(Boolean);

    return faqArray.length ? faqArray : undefined;
}
